import { useState, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { useBreadcrumbs } from "../BreadcrumbContext";
import {
  Search,
  Layers,
  GitBranch,
  CheckSquare,
  Rocket,
  ArrowRight,
  Sparkles,
  BookOpen,
  ArrowLeft,
  Loader2,
  Network,
  Plus
} from "lucide-react";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePhases, usePhaseChildren } from "@/hooks/useLocalStorage";
import { AddWorkflowTemplateModal } from "./AddWorkflowTemplateModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface PhaseItem {
  id: number;
  entityType?: string;
  key?: string;
  name: string;
  description?: string;
  displayName?: string;
  parentType?: string;
  parentCode?: number | string;
  seq?: number;
  useFlag?: number;
  createdAt?: string | null;
  createdBy?: string | null;
  isDeleted?: boolean;
}

const getPhaseTheme = (index: number) => {
  const themes = [
    {
      icon: Layers,
      iconColor: "text-blue-600 bg-blue-100",
      colorClass: "border-slate-200 hover:border-blue-300 hover:bg-blue-50/20",
    },
    {
      icon: GitBranch,
      iconColor: "text-indigo-600 bg-indigo-100",
      colorClass: "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/20",
    },
    {
      icon: CheckSquare,
      iconColor: "text-emerald-600 bg-emerald-100",
      colorClass: "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/20",
    },
    {
      icon: Rocket,
      iconColor: "text-violet-600 bg-violet-100",
      colorClass: "border-slate-200 hover:border-violet-300 hover:bg-violet-50/20",
    },
    {
      icon: Sparkles,
      iconColor: "text-amber-600 bg-amber-100",
      colorClass: "border-slate-200 hover:border-amber-300 hover:bg-amber-50/20",
    },
    {
      icon: BookOpen,
      iconColor: "text-rose-600 bg-rose-100",
      colorClass: "border-slate-200 hover:border-rose-300 hover:bg-rose-50/20",
    }
  ];
  return themes[index % themes.length];
};

interface WorkflowPhaseDetailProps {
  phase: PhaseItem;
  onBack: () => void;
}

function WorkflowPhaseDetail({ phase, onBack }: WorkflowPhaseDetailProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: childrenData, isLoading, isError, error } = usePhaseChildren(phase.name);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const items = childrenData?.items || [];

  const filteredData = items.filter((item: any) => {
    const term = searchTerm.toLowerCase();
    return (
      (item.name && item.name.toLowerCase().includes(term)) ||
      (item.description && item.description.toLowerCase().includes(term)) ||
      (item.workflow_key && item.workflow_key.toLowerCase().includes(term)) ||
      (item.id && String(item.id).includes(term))
    );
  });

  return (
    <div className="space-y-6 animate-fade-in max-w-8xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            {phase.displayName || phase.name}
          </h2>
          {phase.description && (
            <p className="text-gray-600 mt-1 text-sm md:text-base leading-relaxed">
              {phase.description}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Library
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full max-w-md bg-white rounded-lg border border-slate-200 px-3 py-2 shadow-xs focus-within:border-workfinity-300 focus-within:ring-1 focus-within:ring-workfinity-300 transition-all">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search items..."
            className="w-full bg-transparent border-none outline-none text-sm text-slate-800 placeholder-slate-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Template
        </Button>
      </div>

      <AddWorkflowTemplateModal
        isOpen={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        phaseName={phase.name}
      />

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-workfinity-500" />
          <p className="text-sm text-slate-500">Loading details...</p>
        </div>
      ) : isError ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 shadow-xs">
          <h2 className="text-lg font-semibold text-slate-900">Failed to load data</h2>
          <p className="text-sm text-slate-500 mt-1">{(error as Error)?.message || "An error occurred."}</p>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 shadow-xs">
          <h2 className="text-lg font-semibold text-slate-900">No items found</h2>
          <p className="text-sm text-slate-500 mt-1">
            {searchTerm ? "No matching records found for your search." : "This phase has no template items."}
          </p>
        </div>
      ) : (
        <div className="border bg-white border-slate-200 rounded-xl overflow-hidden shadow-xs">
          <Table>
            <TableHeader className="bg-slate-50/70">
              <TableRow>
                <TableHead className="w-[100px] font-semibold text-slate-700">ID</TableHead>
                <TableHead className="font-semibold text-slate-700">Name</TableHead>
                <TableHead className="font-semibold text-slate-700">Workflow Key</TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-700">Build</TableHead>
                <TableHead className="max-w-[400px] font-semibold text-slate-700">Description</TableHead>
                <TableHead className="w-[180px] font-semibold text-slate-700">Date Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((item: any) => {
                const formattedDate = item.date_created
                  ? new Date(item.date_created).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                  : "-";

                return (
                  <TableRow key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <TableCell className="text-sm text-slate-500 font-semibold">{item.id}</TableCell>
                    <TableCell className="text-sm font-semibold text-slate-900">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 font-mono text-sm">
                        {item.workflow_key}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => {
                          if (item.id) {
                            window.open(
                              ` https://workflow-designer.workfinity.com/workflow/template/${item.id}`,
                              "_blank"
                            );
                          }
                        }}
                        className="p-1.5 border border-slate-200 inline-flex items-center justify-center bg-white rounded-md hover:bg-slate-50 hover:border-slate-300 transition-all shadow-2xs cursor-pointer"
                        title="Open in Workflow Designer"
                      >
                        <Network
                          size={18}
                          strokeWidth={2.5}
                          className="text-yellow-500"
                        />
                      </button>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm whitespace-pre-wrap leading-relaxed max-w-[400px]">
                      {item.description || "-"}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{formattedDate}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function WorkflowTemplateLibrary() {
  const [match, params] = useRoute("/workflow-templates/:phase");
  const [, setLocation] = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { data: phasesData, isLoading, isError, error } = usePhases();

  const selectedPhaseName = match && params?.phase ? decodeURIComponent(params.phase) : null;
  const selectedPhase = phasesData?.find((p) => p.name === selectedPhaseName) || null;

  useEffect(() => {
    if (selectedPhase) {
      setBreadcrumbs([
        { label: "Workflow Templates", path: "/workflow-templates" },
        { label: selectedPhase.displayName || selectedPhase.name, path: `/workflow-templates/${encodeURIComponent(selectedPhase.name)}` }
      ]);
    } else {
      setBreadcrumbs([{ label: "Workflow Templates", path: "/workflow-templates" }]);
    }
  }, [selectedPhase, setBreadcrumbs]);

  if (selectedPhaseName) {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-workfinity-500" />
          <p className="text-sm text-slate-500">Loading details...</p>
        </div>
      );
    }
    if (isError) {
      return (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 shadow-xs max-w-8xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900">Failed to load templates</h2>
          <p className="text-sm text-slate-500 mt-1">{(error as Error)?.message || "An error occurred."}</p>
          <Button variant="outline" className="mt-4" onClick={() => setLocation("/workflow-templates")}>
            Back to Library
          </Button>
        </div>
      );
    }
    if (!selectedPhase) {
      return (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 shadow-xs max-w-8xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900">Phase Not Found</h2>
          <p className="text-sm text-slate-500 mt-1">The requested template phase "{selectedPhaseName}" could not be found.</p>
          <Button variant="outline" className="mt-4" onClick={() => setLocation("/workflow-templates")}>
            Back to Library
          </Button>
        </div>
      );
    }
    return <WorkflowPhaseDetail phase={selectedPhase} onBack={() => setLocation("/workflow-templates")} />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-8xl mx-auto">
        <div className="border-b border-slate-100 pb-5">
          <div className="h-8 w-64 bg-slate-200 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-96 bg-slate-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((n) => (
            <Card key={n} className="h-64 border-slate-200 bg-white animate-pulse">
              <div className="p-5 flex flex-col justify-between h-full">
                <div>
                  <div className="h-10 w-10 bg-slate-200 rounded-xl mb-4"></div>
                  <div className="h-4 w-20 bg-slate-200 rounded mb-2"></div>
                  <div className="h-6 w-32 bg-slate-200 rounded mb-3"></div>
                  <div className="h-4 w-full bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 w-2/3 bg-slate-200 rounded"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-slate-200 p-8 shadow-xs max-w-8xl mx-auto">
        <h2 className="text-lg font-semibold text-slate-900">Failed to load templates</h2>
        <p className="text-sm text-slate-500 mt-1">{(error as Error)?.message || "An error occurred."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-8xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Workflow Template Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Accelerate your processes with pre-configured step sequences, decision branches, and role mappings.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(phasesData || []).map((phase: PhaseItem, index: number) => {
          const { icon: IconComp, iconColor, colorClass } = getPhaseTheme(index);
          return (
            <Card
              key={phase.id}
              onClick={() => setLocation(`/workflow-templates/${encodeURIComponent(phase.name)}`)}
              className={`group flex flex-col justify-between bg-white shadow-xs hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden hover:-translate-y-1 relative cursor-pointer ${colorClass}`}
            >
              <div className="p-5 flex-1 flex flex-col">
                {/* Top indicator bar */}
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-2.5 rounded-xl ${iconColor} transition-transform duration-300 group-hover:scale-110`}>
                    <IconComp className="h-5 w-5" />
                  </div>
                </div>

                {/* Header text */}
                <div className="mb-2">
                  <span className="text-sm font-semibold uppercase tracking-wider text-slate-400">
                    {phase.name}
                  </span>
                  <CardTitle className="text-lg font-bold text-slate-900 mt-0.5 group-hover:text-workfinity-700 transition-colors">
                    {phase.displayName || phase.name}
                  </CardTitle>
                </div>

                {/* Description */}
                {phase.description && (
                  <CardDescription className="text-slate-500 text-sm leading-relaxed mt-2 flex-1">
                    {phase.description}
                  </CardDescription>
                )}
              </div>

              {/* Footer action link */}
              <div className="px-5 py-3.5 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between text-sm font-semibold text-workfinity-600 group-hover:text-workfinity-800 transition-colors cursor-pointer group-hover:bg-slate-50">
                <span>Explore</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
